// Mock auth service for development

// Helper function to simulate API delay
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

// Mock user data
const mockUser = {
  id: '1',
  name: 'Development User',
  email: 'dev@example.com',
  role: 'admin',
  permissions: [
    'system-customer-management',
    'system-customer-management-action-create',
    'system-customer-management-action-edit',
    'system-customer-management-action-delete',
  ],
};

// Mock tokens
const mockTokens = {
  access_token: 'mock_access_token',
  refresh_token: 'mock_refresh_token',
};

class MockAuthService {
  constructor() {
    this.tokens = null;
    this.user = null;

    // Check if already logged in
    if (localStorage.getItem('mockLoggedIn') === 'true') {
      this.tokens = mockTokens;
      this.user = mockUser;
    }

    // Mock configuration
    this.config = {
      clientId: 'mock-client-id',
      authority: 'https://mock-auth-provider.com',
      scopes: ['openid', 'profile', 'email', 'tenants', 'roles'],
      response_mode: 'form_post',
    };
  }

  async initiateLogin() {
    // In a real app, this would redirect to OAuth provider
    // For mock, we'll just simulate a delay and then redirect to callback
    await delay(1000);

    // Store PKCE code verifier (simulating PKCE flow)
    sessionStorage.setItem('code_verifier', 'mock_code_verifier');

    // Store state (simulating CSRF protection)
    sessionStorage.setItem('auth_state', 'mock_state');

    // Simulate the redirect with query parameters
    window.location.href = '/auth/callback?code=mock_auth_code&state=mock_state';
  }

  async handleAuthCallback(code, state) {
    // Verify state (simulating CSRF protection)
    const storedState = sessionStorage.getItem('auth_state');
    sessionStorage.removeItem('auth_state');

    if (state !== storedState) {
      throw new Error('Invalid state parameter');
    }

    // Get code verifier (simulating PKCE)
    const codeVerifier = sessionStorage.getItem('code_verifier');
    sessionStorage.removeItem('code_verifier');

    if (!codeVerifier) {
      throw new Error('Code verifier not found');
    }

    // Simulate API delay
    await delay(800);

    // Set tokens and user
    this.tokens = mockTokens;
    this.user = mockUser;

    // Store login state in localStorage for persistence
    localStorage.setItem('mockLoggedIn', 'true');

    return this.user;
  }

  async fetchUserInfo() {
    // Simulate API delay
    await delay(300);

    this.user = mockUser;
    return this.user;
  }

  setTokens(tokens) {
    this.tokens = tokens;
  }

  async getRefreshToken() {
    return this.tokens?.refresh_token || null;
  }

  async refreshToken() {
    // Simulate API delay
    await delay(500);

    // Return new mock tokens
    this.tokens = {
      ...mockTokens,
      access_token: `mock_access_token_${Date.now()}`,
    };

    return this.tokens.access_token;
  }

  async logout() {
    // Simulate API delay
    await delay(300);

    // Clear tokens and user
    this.tokens = null;
    this.user = null;

    // Remove login state from localStorage
    localStorage.removeItem('mockLoggedIn');
  }

  async getCurrentUser() {
    if (this.user) {
      return this.user;
    }

    if (localStorage.getItem('mockLoggedIn') === 'true') {
      await this.fetchUserInfo();
      return this.user;
    }

    return null;
  }

  // Helper methods
  base64UrlEncode() {
    return 'mock_base64url_encoded_string';
  }

  generateRandomState() {
    return 'mock_state';
  }
}

export default new MockAuthService();
